import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useAuthStore from "../../../store/useAuthStore";
import { IoArrowBack } from "react-icons/io5";

const PROJECT_STATUSES = [
  { id: "planning", name: "Planning" },
  { id: "active", name: "Active" },
  { id: "on_hold", name: "On Hold" },
  { id: "completed", name: "Completed" },
  { id: "cancelled", name: "Cancelled" },
];

const EditProject = () => {
  const { token } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const project = location.state?.project;

  const [name, setName] = useState(project?.name || "");
  const [description, setDescription] = useState(project?.description || "");
  const [status, setStatus] = useState(project?.status || "planning");
  const [startDate, setStartDate] = useState(
    project?.startDate ? project.startDate.split("T")[0] : ""
  );
  const [endDate, setEndDate] = useState(
    project?.endDate ? project.endDate.split("T")[0] : ""
  );
  const [budget, setBudget] = useState(project?.budget || "");
  const [department, setDepartment] = useState(
    project?.department?._id || project?.department || ""
  );
  const [manager, setManager] = useState(
    project?.manager?._id || project?.manager || ""
  );
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    if (!project) {
      navigate("/Projects");
      return;
    }
    fetchDepartments();
    fetchEmployees();
  }, []);

  const fetchDepartments = async () => {
    try {
      const response = await fetch("/api/getAllDepartments", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setDepartments(data.departments || data || []);
      }
    } catch (err) {
      // Departments may not be available
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await fetch("/api/getAllStaffInfo", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setEmployees(data.employees || data.staffInfo || data || []);
      }
    } catch (err) {
      // Employees may not be available
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setErrorMessage("Project name is required");
      setTimeout(() => setErrorMessage(""), 3000);
      return;
    }
    if (!startDate) {
      setErrorMessage("Start date is required");
      setTimeout(() => setErrorMessage(""), 3000);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/updateProject/${project._id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name,
          description: description || undefined,
          status,
          startDate,
          endDate: endDate || undefined,
          budget: budget ? Number(budget) : undefined,
          department: department || undefined,
          manager: manager || undefined,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        setSuccessMessage("Project updated successfully");
        setTimeout(() => navigate("/Projects"), 1500);
      } else {
        setErrorMessage(data.error || "Failed to update project");
        setTimeout(() => setErrorMessage(""), 3000);
      }
    } catch (err) {
      setErrorMessage("Failed to update project");
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  if (!project) return null;

  return (
    <div className="m-2 md:m-6 p-2 md:p-6">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate("/Projects")}
          className="p-2 rounded-full hover:bg-gray-100"
        >
          <IoArrowBack className="text-xl text-primary" />
        </button>
        <h2 className="text-xl font-semibold text-gray-800">
          Edit Project - {project.projectCode || project.name}
        </h2>
      </div>

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {errorMessage}
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
          {successMessage}
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block font-medium mb-2 text-blue-500">
              Project Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block font-medium mb-2 text-blue-500">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {PROJECT_STATUSES.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block font-medium mb-2 text-blue-500">
              Start Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block font-medium mb-2 text-blue-500">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block font-medium mb-2 text-blue-500">
              Budget (Nu)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block font-medium mb-2 text-blue-500">
              Department
            </label>
            <select
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Department</option>
              {Array.isArray(departments) &&
                departments.map((dept) => (
                  <option key={dept._id} value={dept._id}>
                    {dept.deptName || dept.name}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label className="block font-medium mb-2 text-blue-500">
              Manager
            </label>
            <select
              value={manager}
              onChange={(e) => setManager(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Manager</option>
              {Array.isArray(employees) &&
                employees.map((emp) => (
                  <option key={emp._id} value={emp._id}>
                    {emp.name || `${emp.firstName || ""} ${emp.lastName || ""}`.trim()}
                  </option>
                ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block font-medium mb-2 text-blue-500">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={() => navigate("/Projects")}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:opacity-90 disabled:opacity-50"
          >
            {loading ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditProject;
